/**
 * Calendar Settings Dropdown
 *
 * Popover menu for calendar display settings:
 * - Time format (blocks vs hourly)
 * - Show moon phases toggle
 */

import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { Toggle } from '@/components/ui/Toggle';
import { useCalendarSettingsStore, TimeFormat } from '@/stores/calendarSettingsStore';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';

interface SettingsDropdownProps {
  onDownload?: () => void;
  onReset?: () => void;
  onManageHolidays?: () => void;
  onImportICS?: () => void;
  onManageMappingRules?: () => void;
}

export const SettingsDropdown = ({
  onDownload,
  onReset: onResetProp,
  onManageHolidays: onManageHolidaysProp,
  onImportICS: onImportICSProp,
  onManageMappingRules: onManageMappingRulesProp,
}: SettingsDropdownProps) => {
  const { t } = useTranslation(['calendar', 'common']);
  // Reset, holidays, ICS import and mapping rules change the calendar itself: reviewer-only
  // people (who may only look) do not get them. Download stays (read only).
  const { canEditCalendar } = useReviewerRestrictions();
  const onReset = canEditCalendar ? onResetProp : undefined;
  const onManageHolidays = canEditCalendar ? onManageHolidaysProp : undefined;
  const onImportICS = canEditCalendar ? onImportICSProp : undefined;
  const onManageMappingRules = canEditCalendar ? onManageMappingRulesProp : undefined;
  const { timeFormat, showMoonPhases, updateSettings } = useCalendarSettingsStore();

  const handleTimeFormatChange = (format: TimeFormat) => {
    updateSettings({ timeFormat: format });
  };

  const handleMoonPhasesToggle = () => {
    updateSettings({ showMoonPhases: !showMoonPhases });
  };

  return (
    <Popover.Root>
      <Tooltip content={t('calendar_settings', { defaultValue: 'Calendar settings' })}>
        <Popover.Trigger asChild>
          <button
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Calendar settings"
          >
            <FontAwesomeIcon icon={faGear} className="text-gray-600 text-sm" />
          </button>
        </Popover.Trigger>
      </Tooltip>

      <Popover.Portal>
        <Popover.Content
          className="
            bg-white rounded-lg shadow-lg border border-gray-200
            w-64 lg:w-80 max-h-[70vh] overflow-y-auto p-3 lg:p-4 z-50
            data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95
            data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95
            data-[side=top]:slide-in-from-bottom-2
            data-[side=bottom]:slide-in-from-top-2
          "
          sideOffset={5}
          align="end"
          collisionPadding={16}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="space-y-2 lg:space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between pb-1.5 lg:pb-2 border-b">
              <h3 className="text-xs lg:text-sm font-semibold text-gray-900">{t('calendar_settings')}</h3>
              <Popover.Close asChild>
                <button
                  className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"
                  aria-label={t('close_settings')}
                >
                  <Icon name={ICONS.CROSS} size="xs" ariaHidden />
                </button>
              </Popover.Close>
            </div>

            {/* Actions Section */}
            {(onDownload || onReset || onManageHolidays || onImportICS || onManageMappingRules) && (
              <div className="space-y-0.5 pb-2 lg:pb-3 border-b border-gray-200">
                {onDownload && (
                  <Popover.Close asChild>
                    <button
                      onClick={onDownload}
                      className="w-full flex items-center gap-2 lg:gap-3 px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon name={ICONS.DOWNLOAD} size="xs" ariaHidden />
                      {t('download_calendar')}
                    </button>
                  </Popover.Close>
                )}

                {onReset && (
                  <Popover.Close asChild>
                    <button
                      onClick={onReset}
                      className="w-full flex items-center gap-2 lg:gap-3 px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon name={ICONS.REFRESH} size="xs" ariaHidden />
                      {t('reset_calendar')}
                    </button>
                  </Popover.Close>
                )}

                {onManageHolidays && (
                  <Popover.Close asChild>
                    <button
                      onClick={onManageHolidays}
                      className="w-full flex items-center gap-2 lg:gap-3 px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon name={ICONS.CALENDAR} size="xs" ariaHidden />
                      {t('manage_holidays')}
                    </button>
                  </Popover.Close>
                )}

                {onImportICS && (
                  <Popover.Close asChild>
                    <button
                      onClick={onImportICS}
                      className="w-full flex items-center gap-2 lg:gap-3 px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon name={ICONS.UPLOAD} size="xs" ariaHidden />
                      {t('import_ics')}
                    </button>
                  </Popover.Close>
                )}

                {onManageMappingRules && (
                  <Popover.Close asChild>
                    <button
                      onClick={onManageMappingRules}
                      className="w-full flex items-center gap-2 lg:gap-3 px-2 lg:px-3 py-1.5 lg:py-2 text-xs lg:text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon name={ICONS.LINK} size="xs" ariaHidden />
                      {t('mapping_rules')}
                    </button>
                  </Popover.Close>
                )}
              </div>
            )}

            {/* Time Format Setting */}
            <div className="space-y-1.5 lg:space-y-2">
              <label className="text-xs lg:text-sm font-medium text-gray-700">
                {t('time_format')}
              </label>
              <div className="flex gap-1.5 lg:flex-col lg:gap-2">
                <Popover.Close asChild>
                  <button
                    onClick={() => handleTimeFormatChange('blocks')}
                    className={`
                      flex-1 lg:w-full px-2 lg:px-3 py-1.5 lg:py-2 text-left text-xs lg:text-sm rounded-lg border
                      transition-colors
                      ${
                        timeFormat === 'blocks'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="font-medium">{t('time_blocks')}</div>
                    <div className="hidden lg:block text-xs text-gray-500">
                      {t('time_blocks_desc')}
                    </div>
                  </button>
                </Popover.Close>
                <Popover.Close asChild>
                  <button
                    onClick={() => handleTimeFormatChange('hourly')}
                    className={`
                      flex-1 lg:w-full px-2 lg:px-3 py-1.5 lg:py-2 text-left text-xs lg:text-sm rounded-lg border
                      transition-colors
                      ${
                        timeFormat === 'hourly'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="font-medium">{t('hourly_slots')}</div>
                    <div className="hidden lg:block text-xs text-gray-500">
                      {t('hourly_slots_desc')}
                    </div>
                  </button>
                </Popover.Close>
              </div>
            </div>

            {/* Moon Phases Setting */}
            <div className="flex items-center justify-between pt-1.5 lg:pt-2 border-t">
              <div className="flex-1">
                <label
                  htmlFor="moon-phases-toggle"
                  className="text-xs lg:text-sm font-medium text-gray-700 cursor-pointer"
                >
                  {t('show_moon_phases')}
                </label>
                <p className="hidden lg:block text-xs text-gray-500 mt-0.5">
                  {t('moon_phases_desc')}
                </p>
              </div>
              <Toggle
                checked={showMoonPhases}
                onChange={handleMoonPhasesToggle}
                ariaLabel={t('show_moon_phases')}
              />
            </div>
          </div>

          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
